import { UnknownCoralError } from '@amzn/ihm-js-coral-client';

import { Observable } from 'rxjs';

export const RESPONSE_SIZE_ERROR_CODE = 504;
export const DEFAULT_PAGE_SIZE = 2000;

type PageFetcher<T> = (pageNumber: number, pageSize: number) => Observable<T>;
type KeysetPageFetcher<T> = (lastEvaluatedId: number | undefined, maxPageSize: number) => Observable<T>;

export interface Paginator<T> {
    /**
     * Gets the next page of data given a page number
     * and page size.
     * @param pageNumber The zero-indexed page number to fetch
     * @param pageSize The page size to use
     */
    nextPage: PageFetcher<T>;

    /**
     * Accepts new page data and decides if additional pages
     * are needed
     * @param t The next page of data
     * @return True if no more data is expected.
     */
    accumulate: (t: T) => boolean;

    /**
     * Downsizes the accumulated data to accommodate page resizing.
     *
     * Page size reduction may be necessary when retrying errors due to issues
     * with response size.
     *
     * If the new page size does not cleanly divide the number of accumulated
     * items, then there is no way to fetch a page that does not have overlap
     * with data that has already been fetched. We can avoid duplicates by
     * downsizing the existing data to a multiple of the new page size.
     *
     * Downsizing should truncate the accumulated data to the given size,
     * retaining data from older pages and potentially discarding data from
     * newer pages as needed.
     *
     * @param newSize The number of items that should be present after resizing.
     */
    downSize: (newSize: number) => void;

    /**
     * Get the number of items accumulated so far.
     */
    getSize: () => number;

    /**
     * Get the accumulated result.
     */
    getValue: () => T;
}

export interface KeysetPaginator<T> {
    /**
     * Gets the next page of data using keyset pagination
     * @param lastEvaluatedId The ID of the last item from the previous page
     * @param maxPageSize The maximum page size to use
     */
    nextPage: KeysetPageFetcher<T>;

    /**
     * Accepts new page data and decides if additional pages are needed
     * @param t The next page of data
     * @return True if no more data is expected
     */
    accumulate: (t: T) => boolean;

    /**
     * Get the number of items accumulated so far
     */
    getSize: () => number;

    /**
     * Get the accumulated result
     */
    getValue: () => T;
}

/**
 * Fetch all data using the given paginator and starting page size.
 * @param paginator The paginator to use.
 * @param pageSize The starting page size to fetch. Smaller page sizes may be
 * used if there are issues with response size.
 */
export function getAllPages<T>(paginator: Paginator<T>, pageSize: number = DEFAULT_PAGE_SIZE): Promise<T> {
    return new Promise((resolve, reject) => getPage(paginator, 0, pageSize, resolve, reject));
}

/**
 * Fetch all data using keyset pagination
 * @param paginator The keyset paginator to use
 * @param pageSize The page size to fetch
 */
export function getAllKeysetPages<T>(paginator: KeysetPaginator<T>, pageSize: number = DEFAULT_PAGE_SIZE): Promise<T> {
    return new Promise((resolve, reject) => getKeysetPage(paginator, undefined, pageSize, resolve, reject));
}

function getKeysetPage<T>(
    paginator: KeysetPaginator<T>,
    lastEvaluatedId: number | undefined,
    pageSize: number,
    resolve: (t: T) => void,
    reject: (e: any) => void
): void {
    paginator.nextPage(lastEvaluatedId, pageSize).subscribe({
        next: (output) => {
            const isComplete = paginator.accumulate(output);
            if (isComplete) {
                resolve(paginator.getValue());
            } else {
                // Get the lastEvaluatedId from the current output for next page
                const currentValue = paginator.getValue() as any;
                const nextLastEvaluatedId = currentValue.lastEvaluatedId;
                getKeysetPage(paginator, nextLastEvaluatedId, pageSize, resolve, reject);
            }
        },
        error: (e: any) => {
            reject(e);
        }
    });
}

function getPage<T>(
    paginator: Paginator<T>,
    page: number,
    pageSize: number,
    resolve: (t: T) => void,
    reject: (e: any) => void
): void {
    paginator.nextPage(page, pageSize).subscribe({
        next: (output) => {
            if (paginator.accumulate(output)) {
                resolve(paginator.getValue());
            } else {
                getPage(paginator, page + 1, pageSize, resolve, reject);
            }
        },
        error: (e: any) => {
            if (e instanceof UnknownCoralError && e.status === RESPONSE_SIZE_ERROR_CODE) {
                const newSize = Math.floor(pageSize / 2);
                if (newSize === 0) {
                    // we can't back off anymore, just let the error bubble up
                    reject(e);
                    return;
                }
                const newPage = Math.floor(paginator.getSize() / newSize);
                paginator.downSize(newPage * newSize); // truncate to avoid overlaps
                getPage(paginator, newPage, newSize, resolve, reject);
            } else {
                reject(e);
            }
        }
    });
}
